import { useEffect } from "react";

interface DocumentMeta {
  /** The string to set as document.title while this hook's component is mounted. */
  title: string;
  /** Optional description — when present, also sets <meta name="description"> + og:title / og:description / og:type="article". */
  description?: string;
  /**
   * Optional canonical address — sets <link rel="canonical"> and og:url, so search engines index one address for a
   * page reachable at several (a knowledge base article by id and by slug).
   */
  canonicalUrl?: string;
}

/**
 * Side-effect hook that sets `document.title` and a small set of SEO meta tags
 * (`description`, `og:title`, `og:description`, `og:type="article"`) while the
 * consuming component is mounted, and cleans up on unmount.
 *
 * Avoids pulling in `react-helmet-async` for a small number of pages. If meta tags
 * already exist in the document head (e.g. static defaults in index.html), their
 * previous content is preserved — the hook only removes tags it created itself.
 */
export function useDocumentMeta({ title, description, canonicalUrl }: DocumentMeta) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const createdTags: HTMLMetaElement[] = [];
    const restoreContent: Array<{ el: HTMLMetaElement; content: string | null }> = [];

    const upsertMeta = (attr: "name" | "property", key: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (el) {
        // Element existed before we mounted — remember its previous content so we can restore on unmount.
        restoreContent.push({ el, content: el.getAttribute("content") });
        el.setAttribute("content", content);
      } else {
        // We're creating this tag; remember it so we can remove it on unmount.
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute("content", content);
        document.head.appendChild(el);
        createdTags.push(el);
      }
    };

    if (description) {
      upsertMeta("name", "description", description);
      upsertMeta("property", "og:title", title);
      upsertMeta("property", "og:description", description);
      upsertMeta("property", "og:type", "article");
    }

    let canonicalLink: HTMLLinkElement | null = null;
    let previousCanonical: string | null = null;
    let createdCanonical = false;
    if (canonicalUrl) {
      upsertMeta("property", "og:url", canonicalUrl);
      canonicalLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (canonicalLink) {
        previousCanonical = canonicalLink.getAttribute("href");
      } else {
        canonicalLink = document.createElement("link");
        canonicalLink.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalLink);
        createdCanonical = true;
      }
      canonicalLink.setAttribute("href", canonicalUrl);
    }

    return () => {
      document.title = prevTitle;
      if (canonicalLink) {
        if (createdCanonical) canonicalLink.remove();
        else if (previousCanonical === null) canonicalLink.removeAttribute("href");
        else canonicalLink.setAttribute("href", previousCanonical);
      }
      // Remove tags we created; restore previous content on tags we only mutated.
      createdTags.forEach((el) => el.remove());
      restoreContent.forEach(({ el, content }) => {
        if (content === null) {
          el.removeAttribute("content");
        } else {
          el.setAttribute("content", content);
        }
      });
    };
  }, [title, description, canonicalUrl]);
}
